/**
 * Positions.js — Tax Lots, Trades & Position Actions
 * ====================================================
 * Enhanced with:
 *  - Convert modal (a note or a preferred into common, via the notice engine)
 *  - Sell equity modal (with fee calculation preview)
 *  - Delivery lifecycle actions (Mark DWAC Pending, Mark Delivered)
 *  - Accrue OID / Accrue Dividend buttons
 *  - Security instrument-type awareness
 */

import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { queryParam } from "../components/links";
import { ExportCsvButton } from "../components/PageTools";
import ConfirmDialog from "../components/ConfirmDialog";
import api from "../api/client";
import FinTable from "../components/FinTable";
import { CLOSED_DEALS, TABS, filterLots, hiddenFromClosedDeals } from "../components/positionFilters";

// #13: Show N/A for unit cost when quantity is zero
const fmtUnitCost = (lot) => {
  if (!lot.quantity || parseFloat(lot.quantity) === 0) return "N/A";
  return "$" + parseFloat(lot.unit_cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4});
};

/* ───────── colour tokens (match app dark theme) ────────── */
const C = {
  bg: "#0f1724",
  card: "#1a2332",
  cardHover: "#1e293b",
  border: "#2a3a4e",
  text: "#e2e8f0",
  muted: "#94a3b8",
  accent: "#3b82f6",
  accentHover: "#2563eb",
  green: "#22c55e",
  greenBg: "rgba(34,197,94,.12)",
  red: "#ef4444",
  redBg: "rgba(239,68,68,.12)",
  orange: "#f59e0b",
  orangeBg: "rgba(245,158,11,.12)",
  purple: "#a855f7",
  purpleBg: "rgba(168,85,247,.12)",
  cyan: "#06b6d4",
  cyanBg: "rgba(6,182,212,.12)",
  teal: "#14b8a6",
  tealBg: "rgba(20,184,166,.12)",
};

/* ───────── small reusable components ────────── */
const Badge = ({ children, bg, color }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      background: bg,
      color,
      textTransform: "uppercase",
      letterSpacing: ".5px",
    }}
  >
    {children}
  </span>
);

const ActionBtn = ({ children, onClick, color = C.accent, disabled = false, small = false, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      padding: small ? "3px 8px" : "5px 12px",
      fontSize: small ? 11 : 12,
      fontWeight: 600,
      borderRadius: 4,
      border: `1px solid ${color}`,
      background: "transparent",
      color,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
      transition: "all .15s",
      whiteSpace: "nowrap",
      marginRight: 4,
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.target.style.background = color;
        e.target.style.color = "#fff";
      }
    }}
    onMouseLeave={(e) => {
      e.target.style.background = "transparent";
      e.target.style.color = color;
    }}
  >
    {children}
  </button>
);

const fmt = (v) =>
  v != null
    ? Number(v).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      })
    : "—";

const fmtQty = (v) =>
  v != null ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—";

/* ───────── Modal shell ────────── */
const Modal = ({ open, onClose, title, children, width = 520 }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          borderRadius: 12,
          border: `1px solid ${C.border}`,
          width,
          maxWidth: "95vw",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,.5)",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17, color: C.text, fontWeight: 600 }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              fontSize: 20,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: "20px 24px" }}>{children}</div>
      </div>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = "text", placeholder, disabled, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <label
      style={{
        display: "block",
        fontSize: 12,
        fontWeight: 600,
        color: C.muted,
        marginBottom: 6,
        textTransform: "uppercase",
        letterSpacing: ".5px",
      }}
    >
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        background: C.bg,
        color: C.text,
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
        opacity: disabled ? 0.5 : 1,
      }}
    />
    {hint && (
      <span style={{ fontSize: 11, color: C.muted, marginTop: 4, display: "block" }}>
        {hint}
      </span>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".5px" }}>
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, boxSizing: "border-box" }}
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const InfoRow = ({ label, value, color }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
    <span style={{ color: C.muted, fontSize: 13, flexShrink: 0 }}>{label}</span>
    <span style={{ color: color || C.text, fontSize: 13, fontWeight: 500, textAlign: "right" }}>{value}</span>
  </div>
);

const SubmitBtn = ({ onClick, loading, disabled, children, color = C.accent }) => (
  <button
    onClick={onClick}
    disabled={loading || disabled}
    style={{
      width: "100%",
      padding: "12px",
      borderRadius: 8,
      border: "none",
      background: loading || disabled ? C.muted : color,
      color: "#fff",
      fontSize: 14,
      fontWeight: 600,
      cursor: loading || disabled ? "not-allowed" : "pointer",
      marginTop: 12,
    }}
  >
    {loading ? "Processing…" : children}
  </button>
);

const AlertBox = ({ type, children }) => {
  const colors = { error: C.red, success: C.green, info: C.accent, warning: C.orange };
  const bgs = { error: C.redBg, success: C.greenBg, info: "rgba(59,130,246,.12)", warning: C.orangeBg };
  return (
    <div style={{ padding: "10px 14px", borderRadius: 6, background: bgs[type] || bgs.info, border: `1px solid ${colors[type] || colors.info}33`, color: colors[type] || colors.info, fontSize: 13, marginBottom: 14 }}>
      {children}
    </div>
  );
};

/* ───────── action badge helper ────────── */
function actionBadge(action) {
  switch (action) {
    case "Buy":       return <Badge bg={C.greenBg} color={C.green}>Buy</Badge>;
    case "Sell":      return <Badge bg={C.redBg} color={C.red}>Sell</Badge>;
    case "Convert":   return <Badge bg={C.purpleBg} color={C.purple}>Convert</Badge>;
    case "Exercise":  return <Badge bg={C.orangeBg} color={C.orange}>Exercise</Badge>;
    case "Paydown":   return <Badge bg={C.cyanBg} color={C.cyan}>Paydown</Badge>;
    case "ExchangeIn":  return <Badge bg={C.purpleBg} color={C.purple}>Merger</Badge>;
    case "ExchangeOut": return <Badge bg={C.purpleBg} color={C.purple}>Exchanged</Badge>;
    default:         return <Badge bg={C.cyanBg} color={C.cyan}>{action}</Badge>;
  }
}

function deliveryBadge(status) {
  if (!status) return null;
  switch (status) {
    case "DEEMED":       return <Badge bg={C.orangeBg} color={C.orange}>Deemed</Badge>;
    case "PENDING_DWAC": return <Badge bg={C.cyanBg} color={C.cyan}>DWAC Pending</Badge>;
    case "DELIVERED":    return <Badge bg={C.greenBg} color={C.green}>Delivered</Badge>;
    default:             return null;
  }
}


/* ═══════════════════════════════════════════════════════════ */
/*  CONVERT MODAL — a note or a preferred into common          */
/* ═══════════════════════════════════════════════════════════ */
// One dialog for both, posted through the notice engine as post-notices does
// (D-267): the shares go into the deal's common stock at the face (a
// preferred's stated value) converted, the note or preferred releases its
// carrying value, and the difference is realised on the conversion date.
const money = (v, ccy) =>
  v == null
    ? "—"
    : `${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${ccy ? ` ${ccy}` : ""}`;

const todayIso = () => new Date().toISOString().slice(0, 10);

function ConvertModal({ open, onClose, lot, secMap, onSuccess }) {
  const sec = secMap[lot?.security] || {};
  const isPref = (sec.instrument_type || "") === "Preferred";
  const outstanding = lot ? (lot.current_principal || lot.quantity) : null;
  const [amount, setAmount] = useState("");
  const [shares, setShares] = useState("");
  const [tradeDate, setTradeDate] = useState(todayIso());
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && lot) {
      setAmount(outstanding != null ? String(Number(outstanding)) : "");
      setShares("");
      setTradeDate(todayIso());
      setNotes("");
      setPreview(null);
      setError("");
    }
  }, [open, lot, outstanding]);

  // The dry run, whenever the figures are complete. It writes nothing.
  useEffect(() => {
    if (!open || !lot) return undefined;
    if (!(parseFloat(amount) > 0) || !(parseFloat(shares) > 0) || !tradeDate) {
      setPreview(null);
      return undefined;
    }
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const r = await api.post("/positions/convert/", {
          lot_id: lot.id, amount, shares, trade_date: tradeDate, notes, commit: false,
        });
        if (live) { setPreview(r.data); setError(""); }
      } catch (err) {
        if (live) { setPreview(null); setError(err.response?.data?.error || "Dry run failed"); }
      }
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [open, lot, amount, shares, tradeDate, notes]);

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/positions/convert/", {
        lot_id: lot.id, amount, shares, trade_date: tradeDate, notes, commit: true,
      });
      onSuccess();
      onClose();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.reasons) setPreview(data);
      setError(data.error || (data.reasons ? "Not booked" : "Conversion failed"));
    } finally {
      setLoading(false);
    }
  };

  if (!lot) return null;
  const ccy = preview?.converts?.currency || sec.currency || "";
  const reasons = preview?.reasons || [];
  const ready = preview && reasons.length === 0;
  const discount = preview?.discount_earned != null ? Number(preview.discount_earned) : null;

  return (
    <Modal open={open} title={isPref ? "Convert Preferred to Common" : "Convert Note to Common"} onClose={onClose} width={560}>
      {error && <AlertBox type="error">{error}</AlertBox>}

      <div style={{ background: C.bg, borderRadius: 8, padding: 14, marginBottom: 16, border: `1px solid ${C.border}` }}>
        <InfoRow label="Security" value={`${lot.security_ticker} — ${sec.name || lot.deal_name}`} />
        <InfoRow label="Deal" value={lot.deal_name || "—"} />
        <InfoRow label={isPref ? "Stated value outstanding" : "Face outstanding"} value={money(outstanding, ccy)} />
        <InfoRow label="Carrying value" value={money(lot.cost_basis, ccy)} />
      </div>

      <InputField label="Date of conversion" type="date" value={tradeDate} onChange={setTradeDate} />
      <InputField
        label={isPref ? "Stated value converted" : "Face converted"}
        type="number"
        value={amount}
        onChange={setAmount}
        hint={isPref ? "The stated value of the preferred converted, from the conversion notice" : "The principal converted, from the conversion notice"}
      />
      <InputField
        label="Common shares received"
        type="number"
        value={shares}
        onChange={setShares}
        hint="The shares the conversion notice delivers"
      />
      <InputField label="Notes" value={notes} onChange={setNotes} placeholder="Which notice, and why it is entered by hand" />

      {ready && (
        <div style={{ background: C.bg, borderRadius: 8, padding: 14, marginBottom: 8, border: `1px solid ${C.purple}44` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.purple, marginBottom: 8, textTransform: "uppercase" }}>
            What this books
          </div>
          <InfoRow label="Into" value={preview.into || "—"} />
          <InfoRow label="Common shares" value={fmtQty(preview.shares)} color={C.green} />
          <InfoRow label="Cost of the shares" value={money(preview.cost_basis, ccy)} />
          <InfoRow label="Carrying value released" value={money(preview.carrying_released, ccy)} />
          {discount != null && (
            <InfoRow
              label={discount < 0 ? "Premium amortised on conversion" : "Discount earned on conversion"}
              value={money(Math.abs(discount), ccy)}
              color={discount < 0 ? C.red : C.green}
            />
          )}
          {(preview.draws_on || []).map((d, i) => (
            <InfoRow
              key={i}
              label={`Draws on the purchase of ${d.bought_on}`}
              value={`${money(d.principal, ccy)}${d.closes_lot ? " (closes it)" : ""}`}
            />
          ))}
        </div>
      )}

      {reasons.length > 0 && (
        <AlertBox type="warning">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Nothing will be booked:</div>
          {reasons.map((r, i) => <div key={i}>{r}</div>)}
        </AlertBox>
      )}

      <SubmitBtn onClick={submit} loading={loading} disabled={!ready} color={C.purple}>
        Execute Conversion
      </SubmitBtn>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════ */
/*  SELL EQUITY MODAL                                         */
/* ═══════════════════════════════════════════════════════════ */
function SellModal({ open, onClose, lot, onSuccess }) {
  const [quantity, setQuantity] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [commission, setCommission] = useState("0");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [feePreview, setFeePreview] = useState(null);

  useEffect(() => {
    if (open && lot) {
      setQuantity(lot.quantity ? String(Math.floor(Number(lot.quantity))) : "");
      setSalePrice("");
      setCommission("0");
      setNotes("");
      setError("");
      setFeePreview(null);
    }
  }, [open, lot]);

  // Compute P&L preview and fetch fee estimate
  const qty = parseFloat(quantity) || 0;
  const price = parseFloat(salePrice) || 0;
  const comm = parseFloat(commission) || 0;
  const proceeds = qty * price - comm;
  const costPortion = lot ? (qty / Number(lot.quantity)) * Number(lot.cost_basis) : 0;
  const estimatedPnl = proceeds - costPortion;

  useEffect(() => {
    if (!open || !lot?.deal || estimatedPnl <= 0) {
      setFeePreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await api.post("/investors/fee-preview/", {
          deal_id: lot.deal,
          realized_pnl: estimatedPnl.toFixed(2),
        });
        setFeePreview(r.data);
      } catch {
        setFeePreview(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [open, lot, estimatedPnl]);

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/positions/sell-equity/", {
        lot_id: lot.id,
        quantity: qty,
        sale_price: price,
        commission: comm,
        notes,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Sale failed");
    } finally {
      setLoading(false);
    }
  };

  if (!lot) return null;

  return (
    <Modal open={open} title="Sell Equity" onClose={onClose} width={580}>
      {error && <AlertBox type="error">{error}</AlertBox>}

      <div style={{ background: C.bg, borderRadius: 8, padding: 14, marginBottom: 16, border: `1px solid ${C.border}` }}>
        <InfoRow label="Security" value={lot.security_ticker} />
        <InfoRow label="Available Shares" value={fmtQty(lot.quantity)} />
        <InfoRow label="Cost Basis" value={fmt(lot.cost_basis)} />
        <InfoRow label="Unit Cost" value={parseFloat(lot.quantity) === 0 ? "N/A" : `$${Number(lot.unit_cost).toFixed(4)}`} />
        <InfoRow label="Deal" value={lot.deal_name || "—"} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <InputField label="Shares to Sell" type="number" value={quantity} onChange={setQuantity} placeholder="e.g. 10000" />
        <InputField label="Price per Share ($)" type="number" value={salePrice} onChange={setSalePrice} placeholder="e.g. 5.00" />
      </div>
      <InputField label="Commission ($)" type="number" value={commission} onChange={setCommission} placeholder="0" />
      <InputField label="Notes" value={notes} onChange={setNotes} placeholder="Optional…" />

      {/* P&L Preview */}
      {price > 0 && qty > 0 && (
        <div style={{ background: C.bg, borderRadius: 8, padding: 14, marginBottom: 8, border: `1px solid ${estimatedPnl >= 0 ? C.green : C.red}44` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>
            Sale Preview
          </div>
          <InfoRow label="Gross Proceeds" value={fmt(qty * price)} />
          <InfoRow label="Commission" value={fmt(comm)} />
          <InfoRow label="Net Proceeds" value={fmt(proceeds)} />
          <InfoRow label="Cost Allocated" value={fmt(costPortion)} />
          <InfoRow label="Estimated P&L" value={fmt(estimatedPnl)} color={estimatedPnl >= 0 ? C.green : C.red} />
        </div>
      )}

      {/* Fee Preview */}
      {feePreview && Number(feePreview.total_fee) > 0 && (
        <div style={{ background: C.bg, borderRadius: 8, padding: 14, marginBottom: 8, border: `1px solid ${C.purple}44` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.purple, marginBottom: 8, textTransform: "uppercase" }}>
            Incentive Fee Preview
          </div>
          <InfoRow label="Total Fees" value={fmt(feePreview.total_fee)} color={C.purple} />
          <InfoRow label="Deal Team" value={fmt(feePreview.deal_team_total)} />
          <InfoRow label="Management Co" value={fmt(feePreview.mgmt_co_total)} />
          {feePreview.investor_allocations?.slice(0, 4).map((a, i) => (
            <InfoRow key={i} label={`  ${a.investor_name}`} value={fmt(a.total_fee)} />
          ))}
        </div>
      )}

      <SubmitBtn onClick={submit} loading={loading} color={C.red}>
        Execute Sale
      </SubmitBtn>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════ */
/*  DELIVERY STATUS MODAL                                     */
/* ═══════════════════════════════════════════════════════════ */
function DeliveryModal({ open, onClose, lot, onSuccess }) {
  const [settleDate, setSettleDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Determine which action is available
  const currentStatus = lot?.delivery_status || "DEEMED";
  const nextAction = currentStatus === "DEEMED" ? "mark_dwac_pending" : currentStatus === "PENDING_DWAC" ? "mark_delivered" : null;
  const actionLabel = nextAction === "mark_dwac_pending" ? "Mark DWAC Pending" : "Mark Delivered";

  useEffect(() => {
    if (open) {
      setSettleDate(new Date().toISOString().slice(0, 10));
      setError("");
    }
  }, [open]);

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/positions/mark-delivery/", {
        lot_id: lot.id,
        action: nextAction,
        ...(nextAction === "mark_delivered" ? { settle_date: settleDate } : {}),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  if (!lot || !nextAction) return null;

  return (
    <Modal open={open} title={`Delivery: ${actionLabel}`} onClose={onClose} width={480}>
      {error && <AlertBox type="error">{error}</AlertBox>}

      <div style={{ background: C.bg, borderRadius: 8, padding: 14, marginBottom: 16, border: `1px solid ${C.border}` }}>
        <InfoRow label="Lot" value={`#${lot.id} — ${lot.security_ticker}`} />
        <InfoRow label="Shares" value={fmtQty(lot.quantity)} />
        <InfoRow label="Current Status" value={currentStatus} color={C.orange} />
        <InfoRow label="New Status" value={nextAction === "mark_dwac_pending" ? "PENDING_DWAC" : "DELIVERED"} color={C.green} />
      </div>

      {nextAction === "mark_delivered" && (
        <InputField label="Settlement Date" type="date" value={settleDate} onChange={setSettleDate} />
      )}

      {nextAction === "mark_dwac_pending" && (
        <AlertBox type="info">
          This will initiate the DWAC (Deposit/Withdrawal At Custodian) process. The lot will be marked as pending delivery to your prime broker.
        </AlertBox>
      )}

      {nextAction === "mark_delivered" && (
        <AlertBox type="info">
          This confirms shares have been delivered via DWAC. The lot will be unrestricted and available for trading.
        </AlertBox>
      )}

      <SubmitBtn onClick={submit} loading={loading} color={C.teal}>
        {actionLabel}
      </SubmitBtn>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════ */
/*  BUY NOTE MODAL                                            */
/* ═══════════════════════════════════════════════════════════ */
function BuyNoteModal({ open, onClose, onSubmit }) {
  const [form, setForm] = useState({ security: "", deal: "", entity: "", trade_date: new Date().toISOString().slice(0, 10), quantity: "", cost_basis: "", notes: "" });
  const [securities, setSecurities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [entities, setEntities] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/securities/securities/?limit=500").then((r) => setSecurities((r.data.results || r.data).filter((s) => s.instrument_type === "Note")));
      api.get("/securities/deals/?limit=500").then((r) => setDeals(r.data.results || r.data));
      api.get("/core/entities/?limit=100").then((r) => setEntities(r.data.results || r.data));
    }
  }, [open]);

  const upd = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  return (
    <Modal open={open} title="Buy Convertible Note" onClose={onClose}>
      <SelectField label="Security" value={form.security} onChange={(v) => upd("security", v)} placeholder="Select a convertible note…" options={securities.map((s) => ({ value: s.id, label: `${s.ticker} — ${s.name}` }))} />
      <SelectField label="Deal" value={form.deal} onChange={(v) => upd("deal", v)} placeholder="Select deal…" options={deals.map((d) => ({ value: d.id, label: d.deal_name || d.name || `Deal ${d.id}` }))} />
      <SelectField label="Entity" value={form.entity} onChange={(v) => upd("entity", v)} placeholder="Select entity…" options={entities.map((e) => ({ value: e.id, label: e.name }))} />
      <InputField label="Trade Date" type="date" value={form.trade_date} onChange={(v) => upd("trade_date", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <InputField label="Face Value / Quantity" type="number" value={form.quantity} onChange={(v) => upd("quantity", v)} placeholder="e.g. 100000" />
        <InputField label="Purchase Price (cost basis)" type="number" value={form.cost_basis} onChange={(v) => upd("cost_basis", v)} placeholder="e.g. 95000" />
      </div>
      <InputField label="Notes" value={form.notes} onChange={(v) => upd("notes", v)} placeholder="Optional…" />
      <SubmitBtn color={C.green} onClick={() => onSubmit({ security: Number(form.security), deal: Number(form.deal), entity: Number(form.entity), trade_date: form.trade_date, quantity: form.quantity, cost_basis: form.cost_basis, notes: form.notes, strategy_tag: "convertible" })}>
        Buy Note
      </SubmitBtn>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════ */
/*  BUY STOCK MODAL                                           */
/* ═══════════════════════════════════════════════════════════ */
function BuyStockModal({ open, onClose, onSubmit }) {
  const [form, setForm] = useState({ security: "", deal: "", entity: "", trade_date: new Date().toISOString().slice(0, 10), quantity: "", cost_basis: "", notes: "" });
  const [securities, setSecurities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [entities, setEntities] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/securities/securities/?limit=500").then((r) => setSecurities((r.data.results || r.data).filter((s) => s.instrument_type === "Stock")));
      api.get("/securities/deals/?limit=500").then((r) => setDeals(r.data.results || r.data));
      api.get("/core/entities/?limit=100").then((r) => setEntities(r.data.results || r.data));
    }
  }, [open]);

  const upd = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  return (
    <Modal open={open} title="Buy Stock" onClose={onClose}>
      <SelectField label="Security" value={form.security} onChange={(v) => upd("security", v)} placeholder="Select stock…" options={securities.map((s) => ({ value: s.id, label: `${s.ticker} — ${s.name}` }))} />
      <SelectField label="Deal" value={form.deal} onChange={(v) => upd("deal", v)} placeholder="Select deal…" options={deals.map((d) => ({ value: d.id, label: d.deal_name || d.name || `Deal ${d.id}` }))} />
      <SelectField label="Entity" value={form.entity} onChange={(v) => upd("entity", v)} placeholder="Select entity…" options={entities.map((e) => ({ value: e.id, label: e.name }))} />
      <InputField label="Trade Date" type="date" value={form.trade_date} onChange={(v) => upd("trade_date", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <InputField label="Shares" type="number" value={form.quantity} onChange={(v) => upd("quantity", v)} placeholder="e.g. 10000" />
        <InputField label="Total Cost" type="number" value={form.cost_basis} onChange={(v) => upd("cost_basis", v)} placeholder="e.g. 25000" />
      </div>
      <InputField label="Notes" value={form.notes} onChange={(v) => upd("notes", v)} placeholder="Optional…" />
      <SubmitBtn color={C.green} onClick={() => onSubmit({ security: Number(form.security), deal: Number(form.deal), entity: Number(form.entity), trade_date: form.trade_date, quantity: form.quantity, cost_basis: form.cost_basis, notes: form.notes })}>
        Buy Stock
      </SubmitBtn>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════ */
/*  MAIN POSITIONS PAGE                                       */
/* ═══════════════════════════════════════════════════════════ */
export default function Positions() {
  const [lots, setLots] = useState([]);
  const [secMap, setSecMap] = useState({});   // security_id → { instrument_type, name, … }
  const [filter, setFilter] = useState("Open");
  // A search, seeded from `?q=` so the global search and the deal reports can
  // send a reader straight to one instrument's lots (usability review of
  // 23 October 2026, item 3).
  const location = useLocation();
  const [query, setQuery] = useState(() => queryParam(location.search));
  useEffect(() => { setQuery(queryParam(location.search)); }, [location.search]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [buyNoteOpen, setBuyNoteOpen] = useState(false);
  const [buyStockOpen, setBuyStockOpen] = useState(false);
  const [convertLot, setConvertLot] = useState(null);
  const [sellLot, setSellLot] = useState(null);
  const [deliveryLot, setDeliveryLot] = useState(null);

  // Feedback
  const [toast, setToast] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lotsRes, secsRes] = await Promise.all([
        api.get("/positions/tax-lots/?limit=500"),
        api.get("/securities/securities/?limit=500"),
      ]);
      const lotData = lotsRes.data.results || lotsRes.data;
      const secData = secsRes.data.results || secsRes.data;

      // Build security lookup map
      const map = {};
      secData.forEach((s) => {
        map[s.id] = s;
      });
      setSecMap(map);
      setLots(lotData);
    } catch (err) {
      console.error("Failed to load positions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    loadData();
    showToast("Action completed successfully");
  };

  // Filter lots
  //
  // **The middle tab was labelled "Closed" and does not test closedness.** It
  // tests the *deal's* status as well, so a closed lot on a deal still marked
  // Active is in neither of the first two tabs and only appears under All —
  // and a reader who has looked at Open and Closed has every reason to think
  // they have seen the book. §0.17's rule, which this page had wearing its
  // fourth costume: a label may not name a condition the code does not test.
  //
  // The rows it shows are right and are left alone: every sale writes a
  // closing lot, so a tab meaning `!is_open` would bury the positions that
  // actually ended under 293 Hilbert Group disposal rows. What changes is that
  // it now says which question it is answering, and the caption beneath counts
  // what it is holding back.
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchesQuery = (lot) => {
    if (!words.length) return true;
    const sec = secMap[lot.security] || {};
    const text = [lot.security_ticker, sec.ticker, sec.name, sec.issuer, lot.deal_name, lot.action]
      .filter(Boolean).join(" ").toLowerCase();
    return words.every((w) => text.includes(w));
  };
  const filtered = filterLots(lots, filter).filter(matchesQuery);
  const hiddenFromClosed = hiddenFromClosedDeals(lots);

  // Sort: most recent first
  const sorted = [...filtered].sort((a, b) => b.trade_date.localeCompare(a.trade_date));

  const handleVoid = async (lotId, action) => {
    const reason = window.prompt(
      'Void "' + action + '" on lot #' + lotId + '?\n\nThis will reverse GL entries and reopen the lot.\nEnter reason:',
      "Data entry correction"
    );
    if (!reason) return;
    try {
      await api.post("/positions/tax-lots/" + lotId + "/void/", { reason });
      alert("Lot voided successfully. GL entries reversed.");
      window.location.reload();
    } catch (err) {
      alert("Void failed: " + (err.response?.data?.error || err.message));
    }
  };



  // Buy handlers
  const handleBuyNote = async (payload) => {
    try {
      await api.post("/positions/tax-lots/", { ...payload, action: "Buy" });
      setBuyNoteOpen(false);
      handleRefresh();
    } catch (err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : "Failed to create lot");
    }
  };

  const handleBuyStock = async (payload) => {
    try {
      await api.post("/positions/tax-lots/", { ...payload, action: "Buy" });
      setBuyStockOpen(false);
      handleRefresh();
    } catch (err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : "Failed to create lot");
    }
  };

  // Accrue OID (for notes)
  const handleAccrueOID = async (lot) => {
    try {
      await api.post(`/positions/tax-lots/${lot.id}/accrue_oid/`);
      showToast(`Accrued OID for lot #${lot.id}`);
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || "Accrue OID failed", "error");
    }
  };

  // Accrue Dividend (for preferred)
  const handleAccrueDividend = async (lot) => {
    try {
      await api.post(`/positions/tax-lots/${lot.id}/accrue_dividend/`);
      showToast(`Accrued dividend for lot #${lot.id}`);
      loadData();
    } catch (err) {
      showToast(err.response?.data?.error || "Accrue dividend failed", "error");
    }
  };

  /* ──── Determine available actions per lot ──── */
  const getActions = (lot) => {
    const sec = secMap[lot.security] || {};
    const instrType = sec.instrument_type || "";
    const actions = [];

    if (!lot.is_open) return actions; // Closed lots have no actions

    // Buy lots: Convert, Accrue, Sell (for Stock)
    if (lot.action === "Buy") {
      if (instrType === "Note") {
        actions.push({ label: "Convert", color: C.purple, onClick: () => setConvertLot(lot) });
        actions.push({ label: "Accrue OID", color: C.cyan, onClick: () => handleAccrueOID(lot), small: true });
      } else if (instrType === "Preferred") {
        actions.push({ label: "Convert", color: C.purple, onClick: () => setConvertLot(lot) });
        actions.push({ label: "Accrue Div", color: C.cyan, onClick: () => handleAccrueDividend(lot), small: true });
      } else if (instrType === "Stock") {
        actions.push({ label: "Sell", color: C.red, onClick: () => setSellLot(lot) });
      }
    }

    // Convert lots: Sell, Delivery lifecycle
    if (lot.action === "Convert") {
      actions.push({ label: "Sell", color: C.red, onClick: () => setSellLot(lot) });

      // Delivery status actions
      const ds = lot.delivery_status || "DEEMED";
      if (ds === "DEEMED") {
        actions.push({ label: "DWAC", color: C.teal, onClick: () => setDeliveryLot(lot), small: true, title: "Start DWAC delivery" });
      } else if (ds === "PENDING_DWAC") {
        actions.push({ label: "Deliver", color: C.green, onClick: () => setDeliveryLot(lot), small: true, title: "Confirm delivery" });
      }
    }

    return actions;
  };

  /* thStyle/tdStyle removed — now using FinTable */

  return (
    <div style={{ padding: "0 24px 40px" }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 2000,
            padding: "12px 20px",
            borderRadius: 8,
            background: toast.type === "error" ? C.red : C.green,
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            boxShadow: "0 8px 30px rgba(0,0,0,.4)",
            animation: "slideIn .3s ease",
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: C.text, margin: 0 }}>Positions</h1>
          <p style={{ color: C.muted, fontSize: 14, margin: "4px 0 0" }}>Tax lots, trades, and position actions</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ActionBtn color={C.green} onClick={() => setBuyNoteOpen(true)}>+ Buy Note</ActionBtn>
          <ActionBtn color={C.green} onClick={() => setBuyStockOpen(true)}>+ Buy Stock</ActionBtn>
          <ActionBtn color={C.orange} onClick={() => {}}>+ Book Warrant</ActionBtn>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {TABS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "6px 18px",
              borderRadius: 20,
              border: `1px solid ${filter === f ? C.accent : C.border}`,
              background: filter === f ? C.accent : "transparent",
              color: filter === f ? "#fff" : C.muted,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {f}
          </button>
        ))}
        <span style={{ marginLeft: 12, color: C.muted, fontSize: 13, lineHeight: "32px" }}>
          {sorted.length} lot{sorted.length !== 1 ? "s" : ""}
        </span>
        <input
          type="search"
          aria-label="Search lots"
          placeholder="Search ticker, security or deal…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginLeft: "auto", width: 260, maxWidth: "100%", padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, font: "inherit" }}
        />
        <ExportCsvButton
          filename="tax-lots"
          rows={sorted}
          columns={[
            { label: "Trade date", value: (l) => l.trade_date },
            { label: "Action", value: (l) => l.action },
            { label: "Security", value: (l) => l.security_ticker },
            { label: "Name", value: (l) => (secMap[l.security] || {}).name },
            { label: "Deal", value: (l) => l.deal_name },
            { label: "Quantity", value: (l) => l.quantity },
            { label: "Cost basis", value: (l) => l.cost_basis },
            { label: "Realized P&L", value: (l) => l.realized_pnl },
            { label: "Open", value: (l) => (l.is_open ? "yes" : "no") },
          ]}
        />
      </div>

      {/* What this tab is holding back, said on the tab rather than left for
          the reader to discover. A closed lot whose deal is still Active is in
          neither of the first two tabs. */}
      {filter === CLOSED_DEALS && hiddenFromClosed.length > 0 && (
        <div style={{ marginBottom: 12, color: C.muted, fontSize: 12 }}>
          {hiddenFromClosed.length} closed lot{hiddenFromClosed.length === 1 ? "" : "s"}
          {" "}on deals still marked Active {hiddenFromClosed.length === 1 ? "is" : "are"} not
          listed here.{" "}
          <button type="button" onClick={() => setFilter("All")} style={{
            border: 0, background: "none", padding: 0, font: "inherit",
            color: C.accent, textDecoration: "underline", cursor: "pointer",
          }}>Show all lots</button>
        </div>
      )}

      {/* Table */}
      <FinTable
        columns={[
          { key: "trade_date", label: "Date", sortable: true },
          { key: "action", label: "Action", sortable: true,
            render: (v) => actionBadge(v) },
          { key: "security_ticker", label: "Security", sortable: true },
          { key: "_instrument_type", label: "Type", sortable: true,
            render: (_, row) => {
              const sec = secMap[row.security] || {};
              return <span style={{ fontSize: 11, color: C.muted }}>{sec.instrument_type || "\u2014"}</span>;
            }},
          { key: "deal_name", label: "Deal", sortable: true, truncate: 25 },
          { key: "quantity", label: "Qty", align: "right", sortable: true,
            fmt: (v) => fmtQty(v) },
          { key: "cost_basis", label: "Cost Basis", align: "right", sortable: true,
            render: (v) => <span style={{ color: C.green }}>{fmt(v)}</span> },
          { key: "unit_cost", label: "Unit Cost", align: "right", sortable: true,
            fmt: (v) => v != null ? "$" + Number(v).toFixed(4) : "\u2014" },
          { key: "realized_pnl", label: "P&L", align: "right", sortable: true,
            render: (v) => {
              const n = v ? Number(v) : null;
              return <span style={{ color: n > 0 ? C.green : n < 0 ? C.red : C.muted }}>
                {n != null ? fmt(n) : "\u2014"}
              </span>;
            }},
          { key: "is_open", label: "Status", sortable: true,
            render: (v) => v
              ? <Badge bg={C.greenBg} color={C.green}>Open</Badge>
              : <Badge bg={C.redBg} color={C.red}>Closed</Badge> },
          { key: "delivery_status", label: "Delivery", sortable: true,
            render: (v) => deliveryBadge(v) },
        ]}
        rows={sorted}
        loading={loading}
        pageSize={25}
        defaultSort={{ key: "trade_date", direction: "desc" }}
        emptyMessage="No positions found."
        actionColumn={(lot) => (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {lot.is_open && lot.action === "Buy" && ((secMap[lot.security]?.instrument_type || "").includes("Note")) && (
                <button onClick={() => setConvertLot(lot)}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                           border: "1px solid #345479", color: "#345479",
                           background: "transparent", cursor: "pointer" }}>
                  Convert
                </button>
              )}
              {lot.is_open && lot.action === "Buy" && ((secMap[lot.security]?.instrument_type || "").includes("Preferred")) && (
                <button onClick={() => setConvertLot(lot)}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                           border: "1px solid #345479", color: "#345479",
                           background: "transparent", cursor: "pointer" }}>
                  Convert
                </button>
              )}
              {lot.is_open && ((secMap[lot.security]?.instrument_type || "").includes("Stock") || lot.action === "Convert") && (
                <button onClick={() => setSellLot(lot)}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                           border: "1px solid #2d6a4f", color: "#2d6a4f",
                           background: "transparent", cursor: "pointer" }}>
                  Sell
                </button>
              )}
              {!lot.is_open && (
                <button onClick={() => handleVoid(lot.id, lot.action)}
                  style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                           border: "1px solid #9b2226", color: "#9b2226",
                           background: "transparent", cursor: "pointer" }}>
                  Void
                </button>
              )}
            </div>
          )}
      />

      {/* Modals */}
      <BuyNoteModal open={buyNoteOpen} onClose={() => setBuyNoteOpen(false)} onSubmit={handleBuyNote} />
      <BuyStockModal open={buyStockOpen} onClose={() => setBuyStockOpen(false)} onSubmit={handleBuyStock} />
      <ConvertModal open={!!convertLot} onClose={() => setConvertLot(null)} lot={convertLot} secMap={secMap} onSuccess={handleRefresh} />
      <SellModal open={!!sellLot} onClose={() => setSellLot(null)} lot={sellLot} onSuccess={handleRefresh} />
      <DeliveryModal open={!!deliveryLot} onClose={() => setDeliveryLot(null)} lot={deliveryLot} onSuccess={handleRefresh} />

      {/* ── Confirmation Dialog (Bug #20) ──────────────────────────────── */}
      <ConfirmDialog
        open={!!confirmDlg}
        title={confirmDlg?.title}
        message={confirmDlg?.message}
        detail={confirmDlg?.detail}
        variant={confirmDlg?.variant || "danger"}
        confirmLabel={confirmDlg?.confirmLabel || "Confirm"}
        onConfirm={confirmDlg?.onConfirm || (() => setConfirmDlg(null))}
        onCancel={() => setConfirmDlg(null)}
      />

    </div>
  );
}
